import { TextInput } from '@core/components/Inputs';
import { alterarParcelasSubsequentes, calculaDiferencaParcelasUtil, gerarParcelas } from '@core/util/parcelaUtil';
import FuseUtils from '@fuse/utils';
import { Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import { Categorias } from 'app/components/Categorias';
import { VendaContext } from 'app/main/venda/VendaContext';
import { confirmarAlert } from 'app/services/alerts';
import { useFormikContext } from 'formik';
import moment from 'moment';
import NP from 'number-precision';
import { vendaApi } from 'app/services/api/vendaApi';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { mapFormValuesToCalculoTotaisRequest } from '../../../utils/vendaCalculoTotaisMapper';
import { DadosEntrega } from '../../DadosEntrega';
import { DadosVenda } from '../../DadosVenda';
import { VendaFormaPagamento } from '../../VendaFormaPagamento';
import { ProdutoServicoList } from '../../VendaProdutoServico';
import { vendaFormStyles } from './styles';

const CadastroVendaForm = () => {
  const formik = useFormikContext();
  const styles = vendaFormStyles();
  const [limitarCategorias] = useContext(VendaContext).limitarCategorias;
  const { values, errors, touched, submitCount } = formik;
  const [modalRetencaoAliquota, setModalRetencaoAliquota] = useState('');

  const modalRetencaoAliquotaFechado = modalRetencaoAliquota === '';
  const calculoTotaisSeqRef = useRef(0);
  const calculoTotaisTimerRef = useRef(null);

  const aplicarRespostaCalculoTotais = (data) => {
    formik.setFieldValue('valorTotal', data.valorTotal);
    (data.produtosServicos || []).forEach((linha) => {
      formik.setFieldValue(`produtosServicos[${linha.indice}].subtotalCalculado`, linha.subtotal);
    });

    let habilitarTipoDesconto = false;
    (data.produtosServicos || []).forEach((linha) => {
      if (linha.subtotal > 0) {
        habilitarTipoDesconto = true;
      }
    });

    formik.setFieldValue('habilitarTipoDesconto', habilitarTipoDesconto);
    if (!habilitarTipoDesconto) {
      formik.setFieldValue('tipoDesconto', undefined);
      formik.setFieldValue('habilitarDesconto', false);
      formik.setFieldValue('valorDesconto', undefined);
    }
  };

  const recalcularTotaisViaApi = (valuesOverride) => {
    const currentValues = valuesOverride || formik.values;
    if (!currentValues?.produtosServicos?.length) {
      return;
    }

    const itemInvalido = currentValues.produtosServicos.some(
      (ps) => ps?.valor === '' || ps?.valor == null || ps?.quantidade === '' || ps?.quantidade == null
    );
    if (itemInvalido) {
      return;
    }

    clearTimeout(calculoTotaisTimerRef.current);
    calculoTotaisTimerRef.current = setTimeout(async () => {
      const seq = ++calculoTotaisSeqRef.current;
      try {
        const payload = mapFormValuesToCalculoTotaisRequest(currentValues);
        const { data } = await vendaApi().calcularTotais(payload);
        if (seq !== calculoTotaisSeqRef.current) {
          return;
        }
        aplicarRespostaCalculoTotais(data);
        calcularDiferencaCategoriasValor(data.valorTotal, currentValues.categorias);
      } catch (err) {
        // Mantém últimos totais válidos; cálculo oficial é sempre no servidor.
      }
    }, 400);
  };

  useEffect(() => {
    recalcularTotaisViaApi();
    calcularDiferencaCategoriasValor(values?.valorTotal, values?.categorias);
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.produtosServicos, values.valorTotal, modalRetencaoAliquotaFechado]);

  const valuesMotivoVenda = values?.motivoVenda;
  useEffect(() => {
    if (valuesMotivoVenda === 'VENDA_ENTREGA_FUTURA' || valuesMotivoVenda === 'VENDA_A_ORDEM') {
      const { produtosServicos } = values;

      produtosServicos.forEach((ps) => {
        if (ps.produtoServico?.servicoId) {
          ps.produtoServico = null;
        }
      });

      formik.setFieldValue('produtosServicos', produtosServicos);
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesMotivoVenda]);

  const resetParcelas = async () => {
    if (values?.pagamento?.parcelamento?.parcelas?.length) {
      formik.setFieldValue(`pagamento.parcelamento.parcelas`, []);
      formik.setFieldValue('pagamento.parcelamento.diferencaParcelas', 0);
    }
  };

  // RNE-228
  const calculaDiferencaParcelas = (e) => {
    let diferencaParcelas = 0;
    let index = -1;

    if (e?.target?.id?.includes('valorParcela')) {
      index = parseInt(e.target.id.substring(e.target.id.indexOf(`[`) + 1, e.target.id.indexOf(']')));
    }

    if (values?.pagamento?.parcelamento?.parcelas?.length) {
      let { parcelas } = values.pagamento.parcelamento;
      const { valorTotal } = values;

      parcelas = parcelas.map((parcela, i) => {
        if (index === i) {
          return { ...parcela, valorParcela: e.target.value };
        }
        return parcela;
      });

      diferencaParcelas = calculaDiferencaParcelasUtil(parcelas, valorTotal);

      formik.setFieldValue('pagamento.parcelamento.diferencaParcelas', diferencaParcelas.toFixed(2));
    }
  };

  const handleChangePagamento = (e) => {
    formik.handleChange(e);

    if (e?.target?.id === 'pagamento.avista.conta') {
      formik.setFieldValue('pagamento.avista.formaPagamento', null);
    }

    if (e?.target?.id === 'pagamento.avista.compraPaga') {
      // o switch e o formik fazem com que o true vire "false"
      if (e.target.value === 'false') {
        formik.setFieldValue('pagamento.avista.dataPagamento', values?.dataCompra);
      }
    }

    if (e?.target?.id === 'pagamento.tipoPagamento') {
      if (e?.target?.value === 'A_VISTA') {
        formik.setFieldValue('pagamento.avista.tipoPagamento', e.target.value);
      }
    }

    if (e?.target?.id?.includes('pagamento.parcelamento.parcelas') && e?.target?.id?.includes('valorParcela')) {
      calculaDiferencaParcelas(e);
    }
    if (e?.target?.id?.includes('quantidade')) {
      resetParcelas();
    }
  };

  const handleGerarParcelas = () => {
    const quantidade = values?.pagamento?.parcelamento?.quantidade || 0;
    const dataPrimeiraParcela = formik?.values?.dataCompra || moment().add(30, 'day');

    const { valorTotal } = values;

    const parcelas = gerarParcelas(quantidade, dataPrimeiraParcela, valorTotal);

    formik.setFieldValue(`pagamento.parcelamento.parcelas`, parcelas);
  };

  const handleChangeConta = (id, value) => {
    formik.setFieldValue(id, value);
  };

  const handleChangeCondicaoPagamento = (e) => {
    formik.setFieldValue('pagamento.avista.tipoPagamento', e.target.value);
    formik.setFieldValue('pagamento.parcelamento.tipoPagamento', e.target.value);

    formik.setFieldValue('unificarCobrancaContratoVenda', false);
    formik.setFieldValue('faturarContratoVendaJuntos', false);

    formik.handleChange(e);
  };

  // RNE-213
  const handleChangeDataPrimeiraParcela = (dataVencimento) => {
    const parcelas = JSON.parse(JSON.stringify(values?.pagamento?.parcelamento?.parcelas));
    if (dataVencimento && parcelas?.length) {
      const primeiraDataVencimento = dataVencimento.clone();

      const novasParcelas = alterarParcelasSubsequentes(parcelas, primeiraDataVencimento);

      formik.setFieldValue('pagamento.parcelamento.parcelas', novasParcelas);
    }
  };

  const handleAddProdutoServico = () => {
    const { produtosServicos } = values;
    produtosServicos.push({ key: FuseUtils.generateGUID() });
    formik.setFieldValue('produtosServicos', produtosServicos);
  };

  const handleRemoveProdutoServico = (produtoServicoRemovido) => {
    calcularValorTotal(undefined, produtoServicoRemovido);
  };

  const calcularDesconto = (
    valorProdutoServico,
    dataVenda,
    promocaoVigenciaInicio,
    promocaoVigenciaFim,
    promocaoValor,
    promocaoTipo
  ) => {
    if (
      promocaoVigenciaInicio &&
      promocaoVigenciaFim &&
      moment(dataVenda).isSameOrAfter(promocaoVigenciaInicio) &&
      moment(dataVenda).isSameOrBefore(promocaoVigenciaFim)
    ) {
      if (promocaoTipo === 'VALOR') {
        valorProdutoServico -= promocaoValor;
      } else if (promocaoTipo === 'PERCENTUAL') {
        valorProdutoServico -= valorProdutoServico * (promocaoValor / 100);
      }
    }

    return valorProdutoServico || 0;
  };

  const calcularValorTotalSemResetarParcela = () => {
    calcularValorTotalDefault(undefined, undefined);
  };

  const calcularValorTotalDefault = async (e) => {
    const id = e?.target?.id;
    const value = e?.target?.value;

    if (id === 'data' && values.produtosServicos) {
      for (let i = 0; i < values.produtosServicos.length; ++i) {
        const produtoServico = values.produtosServicos[i];
        const valor = calcularDesconto(
          produtoServico?.produtoServico?.valor,
          value,
          produtoServico?.produtoServico?.promocaoVigenciaInicio,
          produtoServico?.produtoServico?.promocaoVigenciaFim,
          produtoServico?.produtoServico?.promocaoValor,
          produtoServico?.produtoServico?.promocaoTipo
        );

        formik.setFieldValue(`produtosServicos[${i}].valor`, valor);
      }
    }

    recalcularTotaisViaApi();
  };

  // RNE-369
  const calcularValorTotal = async (e, prodServRemovido) => {
    calcularValorTotalDefault(e, prodServRemovido);
    resetParcelas();
  };

  const exibeConfirmacaoParaAdicionarServicoComIssRetido = (e, key, index) => {
    const { value } = e?.target;
    const { categorias } = values;
    if (value?.issRetido && categorias.length > 1) {
      confirmarAlert({
        mensagem:
          'Ao alterar para este serviço, as categorias informadas serão apagadas e será possível informar apenas uma categoria. Deseja continuar? ',
        confirmar: () => {
          handleChangeProdutosServicos(e, key, index);
        },
        cancelar: () => {
          return false;
        },
      });
    } else {
      handleChangeProdutosServicos(e, key, index);
    }
  };

  const handleChangeProdutosServicos = async (e, key, index) => {
    const { id, value } = e?.target;

    formik.handleChange(e);

    if (id?.includes('quantidade') || id?.includes('valor')) {
      calcularValorTotal(e);
    } else {
      if (id?.includes('produtoServico')) {
        if (value?.servicoFiscal) {
          formik.handleChange({
            target: { id: `produtosServicos[${index}].servicoFiscal`, value: value?.servicoFiscal },
          });
        }

        if (value?.fiscal) {
          formik.handleChange({
            target: { id: `produtosServicos[${index}].fiscal`, value: { ...value?.fiscal, id: null } },
          });
        }
      }

      // RNE-387
      let habilitarFrete = false;
      for (let i = 0; i < values?.produtosServicos?.length || 0; ++i) {
        const produtoServico = values?.produtosServicos[i];

        if (produtoServico.key === key) {
          const [prefix] = e.target.id.split('.');

          formik.setFieldValue(`${prefix}.servico`, undefined);

          if (e.target.value?.valor) {
            const valorProdutoServico = calcularDesconto(
              e.target.value.valor,
              formik.values.data,
              e.target.value?.promocaoVigenciaInicio,
              e.target.value?.promocaoVigenciaFim,
              e.target.value?.promocaoValor,
              e.target.value?.promocaoTipo
            );

            formik.setFieldValue(`${prefix}.valor`, valorProdutoServico);
            calcularValorTotal({ target: { id: `${prefix}.valor`, value: valorProdutoServico } });
          }

          formik.setFieldValue(`${prefix}.quantidade`, 1);

          // formik.setFieldValue(`${prefix}.fiscal`, undefined);

          if (value?.produtoId) {
            habilitarFrete = true;
            formik.setFieldValue(`${prefix}.servico`, undefined);
            delete value.servico;
            break;
          }
        } else if (produtoServico?.produtoServico?.produtoId) {
          habilitarFrete = true;
          delete produtoServico?.produtoServico?.servico;
        }
      }

      if (!habilitarFrete && values?.valorFrete > 0) {
        formik.setFieldValue('valorFrete', '');
        calcularValorTotal({ target: { id: 'valorFrete', value: 0 } });
      }

      formik.setFieldValue('habilitarFrete', habilitarFrete);
    }
  };

  const handleChangeTipoDesconto = (e) => {
    const event = { target: { id: 'tipoDesconto', value: e.target.value } };
    formik.handleChange(event);

    if (!e.target.value) {
      formik.setFieldValue('desconto', undefined);
      formik.setFieldValue('habilitarDesconto', false);
    } else {
      if (event.target.value === ' ') {
        formik.setFieldValue('valorDesconto', '');
      }

      formik.setFieldValue('habilitarDesconto', true);
    }

    calcularValorTotal(event);
  };

  const handleDadosVenda = async (e) => {
    formik.handleChange(e);

    if (e?.target?.id === 'cliente' && e?.target?.value?.tipoPessoa === 'ESTRANGEIRA') {
      formik.setFieldValue('pagamento.formaRecebimento', '');
    }

    if (e?.target?.value?.emailPrincipal) {
      values.emailsCliente[0] = { email: e?.target?.value?.emailPrincipal, principal: true };
    }
  };

  const calcularDiferencaCategorias = (event, index = -1) => {
    const {
      values: { categorias, valorTotal },
      setFieldValue,
    } = formik;

    let totalCategorias = 0;

    categorias.forEach((categoria, i) => {
      if (index !== i) {
        totalCategorias += categoria.valorBruto ? categoria.valorBruto : 0;
      } else {
        totalCategorias += event.target.value ? event.target.value : 0;
      }
    });

    setFieldValue('diferencaCategorias', valorTotal?.toFixed(2) - totalCategorias?.toFixed(2));
  };

  const calcularDiferencaCategoriasValor = async (valorTotal, categorias) => {
    const { setFieldValue } = formik;

    let totalCategorias = 0;

    categorias.forEach((categoria, i) => {
      totalCategorias += categoria.valorBruto ? categoria.valorBruto : 0;
    });

    if (valorTotal === '') {
      valorTotal = 0;
    }

    setFieldValue('diferencaCategorias', valorTotal?.toFixed(2) - totalCategorias?.toFixed(2));
  };

  const _errors = {
    categorias: values?.categorias?.map((categoria, index) => {
      return {
        classe:
          (submitCount > 0 || (touched?.categorias && touched?.categorias[index]?.classe)) &&
          errors?.categorias &&
          errors?.categorias[index]?.classe,
        valorBruto:
          (submitCount > 0 || (touched?.categorias && touched?.categorias[index]?.valorBruto)) &&
          errors?.categorias &&
          errors?.categorias[index]?.valorBruto,
      };
    }),
  };

  return (
    <>
      <DadosVenda
        values={values}
        onChange={handleDadosVenda}
        onBlur={formik.handleBlur}
        errors={errors}
        touched={touched}
        submitCount={submitCount}
        calcularValorTotal={calcularValorTotal}
      />

      <ProdutoServicoList
        id="produtosServicos"
        modalRetencaoAliquota={modalRetencaoAliquota}
        setModalRetencaoAliquota={setModalRetencaoAliquota}
        value={values?.produtosServicos}
        onChange={exibeConfirmacaoParaAdicionarServicoComIssRetido}
        onBlur={formik.handleBlur}
        handleAddProdutoServico={handleAddProdutoServico}
        handleRemoveProdutoServico={handleRemoveProdutoServico}
        errors={errors}
        touched={touched}
        submitCount={submitCount}
        soProduto={values?.motivoVenda === 'VENDA_ENTREGA_FUTURA' || values?.motivoVenda === 'VENDA_A_ORDEM'}
        possuiNotaFiscalTransmitida={values?.possuiNotaFiscalTransmitida}
        possuiCobrancaRegistrada={values?.possuiCobrancaRegistrada}
        atualizarAliquotaProps={{
          isEdicao: values?.id,
        }}
        retencaoAliquotaProps={{
          isEdicao: values?.id,
        }}
        origemIntegracao={values?.origemIntegracao}
      />

      <Grid container xs={12} style={{ marginTop: 16 }}>
        <span className={styles.label}>Município da incidência</span>
        <Grid item xs={12}>
          <FormControl>
            <RadioGroup
              id="municipioIncidencia"
              name="municipioIncidencia"
              onChange={formik.handleChange}
              value={values?.municipioIncidencia}
              row
            >
              <FormControlLabel control={<Radio color="primary" />} value="PRESTADOR" label="Prestador" />
              <FormControlLabel control={<Radio color="primary" />} value="TOMADOR" label="Tomador" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Divider className={styles.divider} />

      <DadosEntrega
        values={values}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        calcularValorTotal={calcularValorTotal}
        handleChangeTipoDesconto={handleChangeTipoDesconto}
        styles={styles}
        errors={errors}
        touched={touched}
        submitCount={submitCount}
      />

      <Grid container spacing={3} className="mt-16 mb-16">
        <Categorias
          categorias={values?.categorias}
          handleChange={formik.handleChange}
          handleChangeValorBruto={(event, index) => {
            formik.handleChange(event);
            calcularDiferencaCategorias(event, index);
          }}
          handleBlur={formik.handleBlur}
          disabledClasse={values?.possuiCobrancaBaixadaOuRemessaGerada}
          errors={_errors}
          showDiferenca
          touched={touched}
          natureza="RECEITA"
          diferencaCategorias={formik.values?.diferencaCategorias}
          limitarCategorias={limitarCategorias}
          origemIntegracao={values?.origemIntegracao}
        />
      </Grid>
      <VendaFormaPagamento
        id="pagamento"
        name="pagamento"
        onBlur={formik.handleBlur}
        onChange={handleChangePagamento}
        values={values}
        errors={errors}
        touched={touched}
        onGerarParcelas={handleGerarParcelas}
        onChangeConta={handleChangeConta}
        onChangeCondicaoPagamento={handleChangeCondicaoPagamento}
        onChangeDataPrimeiraParcela={handleChangeDataPrimeiraParcela}
        submitCount={formik.submitCount}
      />

      <Grid item xs={12} className="mt-16">
        <TextInput
          id="informacoesComplementares"
          name="informacoesComplementares"
          label="Informações Complementares"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={values?.informacoesComplementares}
          maxLength={500}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          disabled={values?.possuiNotaFiscalTransmitida || values?.possuiCobrancaRegistrada}
        />
      </Grid>
    </>
  );
};

export default CadastroVendaForm;
